import {
  FeatureAccessContextProvider,
  useFeatureAccess,
} from "@src/auth/permissions/FeatureAccessContext";
import { useQueryDocumentGroups } from "@src/backpack-console/pages/SiteDashboard/Documents/documentsQueries";
import { isNonStandardDocumentGroupId } from "@src/backpack-console/pages/SiteDashboard/Documents/nonStandardDocumentUtils";
import {
  LeftNavItem,
  LeftNavItemRowTitle,
} from "@src/straps/base/navigation/LeftNav";
import {
  ComponentPropsWithoutRef,
  FunctionComponent,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { usePostDesignDocGroup } from "../../../global_functions/postgrestApi/designDocsQueries";
import { DocumentGroup } from "../../../types";
import { getGroupPath } from "../../pages/SiteDashboard/Documents/documentsUtils";
import ContentEditable from "../ContentEditable";
import {
  BuildingSelectionSubItem,
  BuildingSelectionSubItemProps,
} from "./BuildingSelectionSubItem";

const AddFolderItem: FunctionComponent<
  React.PropsWithChildren<
    {
      parentGroupId: number;
      siteId: number;
    } & Omit<ComponentPropsWithoutRef<typeof LeftNavItem>, "title">
  >
> = ({ parentGroupId, siteId, ...props }) => {
  const ref = useRef<HTMLDivElement>(null);
  const { mutate } = usePostDesignDocGroup(siteId);
  const onEnter = useCallback(
    (value: string) => {
      mutate({
        groupname: value,
        parentGroup: parentGroupId,
      });
    },
    [parentGroupId, mutate]
  );
  const onClick = useCallback(() => {
    ref.current?.focus();
  }, []);
  return (
    <LeftNavItem
      {...props}
      key="add-folder"
      title="Add Folder"
      titleElement={
        <LeftNavItemRowTitle>
          <ContentEditable
            placeholder="Add Folder"
            onEnter={onEnter}
            ref={ref}
          />
        </LeftNavItemRowTitle>
      }
      iconName="add-folder"
      onClick={onClick}
    />
  );
};

type Props = Readonly<Omit<BuildingSelectionSubItemProps, "subPath" | "title">>;
export function BuildingSelectionDocumentsItem(props: Props) {
  return (
    <FeatureAccessContextProvider
      features={["Equipment", "Utilities", "Projects", "Documents"]}
      PageNotAuthorizedElement={
        <BuildingSelectionSubItem
          title="Documents"
          subPath="documents"
          iconName="documents"
          {...props}
        />
      }
    >
      <BuildingSelectionSubItem
        title="Documents"
        subPath="documents"
        iconName="documents"
        {...props}
      >
        <DocumentGroupsSubItems {...props} />
      </BuildingSelectionSubItem>
    </FeatureAccessContextProvider>
  );
}

function DocumentGroupsSubItems(props: Props) {
  const { data: groups } = useQueryDocumentGroups(props.siteId);
  const [parentGroups, subgroupMap] = useMemo(() => {
    const parents: DocumentGroup[] = [];
    const subgroups: Map<number, DocumentGroup[]> = new Map();
    groups?.forEach((group) => {
      if (!group.parentGroup) {
        parents.push(group);
      } else {
        const siblingSubgroups = subgroups.get(group.parentGroup);
        if (!siblingSubgroups) {
          subgroups.set(group.parentGroup, [group]);
        } else {
          siblingSubgroups.push(group);
        }
      }
    });
    return [parents, subgroups];
  }, [groups]);

  const canEditDesignDocs = useFeatureAccess("Documents") === "edit";

  return (
    <>
      {[
        <BuildingSelectionSubItem
          {...props}
          key="all-uploads"
          title="All Uploads"
          iconName="folder"
          subPath="documents"
          exact
        />,
        ...(parentGroups?.map((group) =>
          // we can only create folders for design docs
          !isNonStandardDocumentGroupId(group.id) ? (
            <BuildingSelectionSubItem
              key={group.id}
              {...props}
              title={group.groupname}
              subPath={`documents/${getGroupPath(group)}`}
            >
              {[
                <BuildingSelectionSubItem
                  key="all-folder"
                  {...props}
                  title={`All ${group.groupname}`}
                  iconName="folder"
                  subPath={`documents/${getGroupPath(group)}`}
                  exact
                />,
                ...(subgroupMap
                  .get(group.id)
                  ?.map((subgroup) => (
                    <BuildingSelectionSubItem
                      key={subgroup.id}
                      {...props}
                      title={subgroup.groupname}
                      iconName="folder"
                      subPath={`documents/${getGroupPath(group, subgroup)}`}
                    />
                  )) ?? []),
                canEditDesignDocs && (
                  <AddFolderItem
                    key="add-folder"
                    siteId={props.siteId}
                    parentGroupId={group.id}
                  />
                ),
              ]}
            </BuildingSelectionSubItem>
          ) : (
            <BuildingSelectionSubItem
              key={group.id}
              {...props}
              iconName="folder"
              title={group.groupname}
              subPath={`documents/${getGroupPath(group)}`}
            />
          )
        ) ?? []),
      ]}
    </>
  );
}
